import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Alert,
    Spinner,
    Table,
    Badge,
    Pagination,
} from "react-bootstrap";
import {
    collectionGroup,
    collection,
    query,
    where,
    getFirestore,
    deleteDoc,
    doc,
    setDoc,
    writeBatch,
} from "firebase/firestore";
// getDocs instrumentado: informa cuántas lecturas hace esta pantalla para
// poder atribuir el consumo de Firestore a cada usuario (visible solo para superAdmin).
import { getDocs } from "../../functions/getDocsInstrumentado";
import { establecerOrigenConsumo } from "../../functions/consumoLecturas";
import Swal from "sweetalert2";
import { format } from "date-fns";
import { FaFileExcel, FaDownload, FaFilter, FaSortUp, FaSortDown, FaMapMarkerAlt, FaTrash, FaSyncAlt } from "react-icons/fa";
import * as XLSX from "xlsx";
import firebaseApp from "../../firebase/credenciales";

const db = getFirestore(firebaseApp);

const isQuotaExhaustedError = (err) =>
  err?.code === "resource-exhausted" ||
  String(err?.message || "").toLowerCase().includes("quota exceeded") ||
  String(err?.message || "").toLowerCase().includes("resource-exhausted");

const handleQuotaExhaustedAlert = (err) => {
  if (isQuotaExhaustedError(err)) {
    Swal.fire({
      icon: "warning",
      title: "Cuota de Firestore Excedida",
      text: "Se ha alcanzado el límite diario de operaciones en Firebase Cloud Firestore (Quota Exceeded). Por favor espere a que la cuota del proyecto se restablezca o contacte al administrador.",
      confirmButtonColor: "#f59e0b",
    });
    return true;
  }
  return false;
};

// Función para formatear el período a nombre de mes en español
const formatPeriodo = (ano, periodo) => {
    const mesesEspanol = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    if (!periodo) return ano || '';

    // Extraer el número del mes del período
    let mesNumero;
    if (typeof periodo === 'string') {
        // Si el período viene como "2026-02" o "2026-2"
        if (periodo.includes('-')) {
            const partes = periodo.split('-');
            mesNumero = parseInt(partes[1], 10);
        } else {
            // Si viene solo como "02" o "2"
            mesNumero = parseInt(periodo, 10);
        }
    } else {
        mesNumero = parseInt(periodo, 10);
    }

    // Validar que el número de mes sea válido
    if (isNaN(mesNumero) || mesNumero < 1 || mesNumero > 12) {
        return ano ? `${ano} - ${periodo}` : periodo;
    }

    const nombreMes = mesesEspanol[mesNumero - 1];
    return ano ? `${nombreMes} ${ano}` : nombreMes;
};

// Función para calcular distancia entre dos puntos (Haversine)
const getDistanciaMetros = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
    const R = 6371e3; // Radio tierra en metros
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

// Función para validar si un punto está "en ruta" (Corredor recto con umbral)
// Retorna objeto con estado, color y distancia de desviación
const validarPuntoEnRuta = (puntoLat, puntoLng, ruta) => {
    if (!puntoLat || !puntoLng || !ruta || !ruta.origenLat || !ruta.origenLng || !ruta.destinoLat || !ruta.destinoLng) {
        return { estado: "Sin Datos", color: "secondary", desviacion: 0 };
    }

    const lat = parseFloat(puntoLat);
    const lng = parseFloat(puntoLng);
    const lat1 = parseFloat(ruta.origenLat);
    const lng1 = parseFloat(ruta.origenLng);
    const lat2 = parseFloat(ruta.destinoLat);
    const lng2 = parseFloat(ruta.destinoLng);

    // Distancia total de la ruta
    const distRuta = getDistanciaMetros(lat1, lng1, lat2, lng2);
    if (distRuta === 0) return { estado: "Error Ruta", color: "dark", desviacion: 0 };

    // Calcular distancia del punto a la línea (Cross-track distance)
    // d = |(y2-y1)x0 - (x2-x1)y0 + x2y1 - y2x1| / sqrt((y2-y1)^2 + (x2-x1)^2)
    // Aproximación plana para distancias cortas (suficiente para esto)
    // O usar fórmula esférica cross-track distance

    // Usaremos la fórmula de distancia de punto a recta (Cross Track Distance)
    const R = 6371e3;
    const d13 = getDistanciaMetros(lat1, lng1, lat, lng) / R;
    const brng13 = Math.atan2(Math.sin((lng - lng1) * Math.PI / 180) * Math.cos(lat * Math.PI / 180), Math.cos(lat1 * Math.PI / 180) * Math.sin(lat * Math.PI / 180) - Math.sin(lat1 * Math.PI / 180) * Math.cos(lat * Math.PI / 180) * Math.cos((lng - lng1) * Math.PI / 180));
    const brng12 = Math.atan2(Math.sin((lng2 - lng1) * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180), Math.cos(lat1 * Math.PI / 180) * Math.sin(lat2 * Math.PI / 180) - Math.sin(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.cos((lng2 - lng1) * Math.PI / 180));

    const dXt = Math.asin(Math.sin(d13) * Math.sin(brng13 - brng12)) * R;
    const desviacion = Math.abs(dXt);

    // Umbral de 1000m (1km) por defecto, o lo que tenga la ruta
    const umbral = ruta.umbralMetros || 1000;

    // Verificar si el punto está "entre" el inicio y el fin (Along track distance)
    // Si está muy antes del inicio o muy después del fin, también es "Fuera de Ruta"
    // Pero para simplificar, si la desviación lateral es baja, asumiremos que "pasó cerca"

    if (desviacion <= umbral) {
        return { estado: "En Ruta", color: "success", desviacion };
    } else {
        return { estado: "Fuera de Ruta", color: "danger", desviacion };
    }
};

const formatearFechaReporte = (fechaIso) => {
    if (!fechaIso || typeof fechaIso !== "string") return "";
    const [y, m, d] = fechaIso.split("-");
    if (!y || !m || !d) return fechaIso;
    return `${d}/${m}/${y}`;
};

const CATEGORIAS_REGISTRO = {
    festividad: { label: "Festividad o Celebración", badge: "info" },
    capacitacion: { label: "Capacitación", badge: "dark" },
    comedor: { label: "Comedor", badge: "warning" },
    bus: { label: "Transporte (Pasajeros)", badge: "primary" },
    tracking: { label: "Tracking Bus (GPS)", badge: "secondary" },
    otro: { label: "Otro", badge: "secondary" },
};

const normalizeText = (value = "") => String(value)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

const getCategoriaKeyFromText = (categoriaText = "") => {
    const categoriaNormalizada = normalizeText(categoriaText);

    if (!categoriaNormalizada) return null;
    if (categoriaNormalizada.includes("tracking")) {
        return "tracking";
    }
    if (categoriaNormalizada.includes("festividad") || categoriaNormalizada.includes("celebracion")) {
        return "festividad";
    }
    if (categoriaNormalizada.includes("capacit")) {
        return "capacitacion";
    }
    if (categoriaNormalizada.includes("comedor")) {
        return "comedor";
    }
    if (categoriaNormalizada.includes("transporte") || categoriaNormalizada.includes("bus") || categoriaNormalizada.includes("movilidad") || categoriaNormalizada.includes("ruta")) {
        return "bus";
    }
    if (categoriaNormalizada.includes("otro")) {
        return "otro";
    }

    return null;
};

const getCategoriaRegistro = (recordOrTipo = "") => {
    const record = typeof recordOrTipo === "object" && recordOrTipo !== null
        ? recordOrTipo
        : { tipo: recordOrTipo };

    if (record.tipo === "TRACKING" || record.metadata?.origen === "background_service") {
        return "tracking";
    }

    // Nuevo estándar: metadata.categoria (o categoria en raíz) define la categoría del registro.
    const categoriaKeyFromMetadata = getCategoriaKeyFromText(record.metadata?.categoria || record.categoria || "");
    if (categoriaKeyFromMetadata) {
        return categoriaKeyFromMetadata;
    }

    const tipoNormalizado = normalizeText(record.tipo || "");
    const eventoCategoriaNormalizada = normalizeText(record.metadata?.eventoCategoria || record.eventoCategoria || "");

    const categoriaKeyFromEvento = getCategoriaKeyFromText(eventoCategoriaNormalizada);
    if (categoriaKeyFromEvento) {
        return categoriaKeyFromEvento;
    }

    const categoriaKeyFromTipo = getCategoriaKeyFromText(tipoNormalizado);
    if (categoriaKeyFromTipo) {
        return categoriaKeyFromTipo;
    }

    return "otro";
};

const getCategoriaLabel = (categoriaKey) => CATEGORIAS_REGISTRO[categoriaKey]?.label || CATEGORIAS_REGISTRO.otro.label;
const getCategoriaBadge = (categoriaKey) => CATEGORIAS_REGISTRO[categoriaKey]?.badge || CATEGORIAS_REGISTRO.otro.badge;

const ReporteIngresos = ({ usuario }) => {
    const esSuperAdmin = usuario?.rol === "superAdmin" || String(usuario?.rol || "").toLowerCase() === "superadmin";
    const [rucEmpresa, setRucEmpresa] = useState(usuario?.ruc || "");
    const [fechaInicio, setFechaInicio] = useState(
        format(new Date(), "yyyy-MM-01")
    );
    const [fechaFin, setFechaFin] = useState(
        format(new Date(), "yyyy-MM-dd")
    );
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(null);
    const [marcacionesData, setMarcacionesData] = useState([]);
    const [employeeList, setEmployeeList] = useState([]);
    const [buses, setBuses] = useState([]);
    const [rutas, setRutas] = useState([]);
    const [cecosList, setCecosList] = useState([]); // ✅ Estado para los centros de costo
    const [empresasTransporte, setEmpresasTransporte] = useState([]); // ✅ Lista de empresas únicas
    const [empresaSeleccionada, setEmpresaSeleccionada] = useState("todos"); // ✅ Empresa seleccionada
    const [driversCache, setDriversCache] = useState({}); // Cache para nombres de conductores
    const [tipoRegistro, setTipoRegistro] = useState("todos");
    const [exportando, setExportando] = useState(false);
    const [actualizandoRegistros, setActualizandoRegistros] = useState(new Set());
    const [actualizandoMasivo, setActualizandoMasivo] = useState(false);
    const [registrosSeleccionados, setRegistrosSeleccionados] = useState(new Set());
    const [eliminandoMasivo, setEliminandoMasivo] = useState(false);

    // Estados para paginación y filtros
    const [paginaActual, setPaginaActual] = useState(1);
    const [elementosPorPagina, setElementosPorPagina] = useState(25);
    const [busquedaEmpleado, setBusquedaEmpleado] = useState("");
    const [ordenarPor, setOrdenarPor] = useState("fecha");
    const [ordenAscendente, setOrdenAscendente] = useState(false);

    // Cargar empleados y grupos al iniciar
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                if (!rucEmpresa) return;
                establecerOrigenConsumo("asistencia:ingresos");

                // Cargar empleados de la colección 'users' (donde está el CECO)
                const empleadosRef = collection(db, "users");
                const empleadosQuery = query(empleadosRef, where("rucEmpresa", "==", rucEmpresa));
                const empleadosSnapshot = await getDocs(empleadosQuery);
                const empleados = [];
                empleadosSnapshot.forEach((doc) => {
                    empleados.push({ id: doc.id, ...doc.data() });
                });

                // Ordenar empleados por nombre
                empleados.sort((a, b) => {
                    const nombreA = `${a.nombres} ${a.apellidoPaterno} ${a.apellidoMaterno}`.toLowerCase();
                    const nombreB = `${b.nombres} ${b.apellidoPaterno} ${b.apellidoMaterno}`.toLowerCase();
                    return nombreA.localeCompare(nombreB);
                });

                setEmployeeList(empleados);

                // Cargar Buses
                const busesRef = collection(db, "buses");
                const busesQuery = query(busesRef, where("rucEmpresa", "==", rucEmpresa));
                const busesSnap = await getDocs(busesQuery);
                const busesList = busesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setBuses(busesList);

                // ✅ Extraer empresas de transporte únicas
                const empresasUnicas = [...new Set(busesList.map(b => b.empresaTransporte).filter(Boolean))].sort();
                setEmpresasTransporte(empresasUnicas);

                // Cargar Rutas
                const rutasRef = collection(db, "rutas");
                const rutasQuery = query(rutasRef, where("rucEmpresa", "==", rucEmpresa));
                const rutasSnap = await getDocs(rutasQuery);
                const rutasList = rutasSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setRutas(rutasList);

                // ✅ Cargar CECOs
                const cecosRef = collection(db, `cecos/${rucEmpresa}/cecos`);
                const cecosSnap = await getDocs(cecosRef);
                const listCecos = cecosSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setCecosList(listCecos);

            } catch (err) {
                console.error("Error al cargar datos iniciales:", err);
                if (handleQuotaExhaustedAlert(err)) {
                    setError("Cuota diaria de Firestore excedida (Quota Exceeded).");
                } else {
                    setError("Error al cargar datos iniciales");
                }
            } finally {
                setInitialLoading(false);
            }
        };

        fetchInitialData();
    }, [rucEmpresa]);

    // Función para cargar marcaciones
    const cargarMarcaciones = async () => {
        try {
            setLoading(true);
            setError(null);
            setMarcacionesData([]);
            setRegistrosSeleccionados(new Set());
            establecerOrigenConsumo("asistencia:ingresos");

            // Usar fechas seleccionadas del estado
            // const [year, month] = selectedMonth.split("-");
            // const fechaInicio = ...
            // const fechaFin = ...

            // Consulta para obtener marcaciones
            const marcacionesQuery = query(
                collectionGroup(db, "registros"),
                where("ruc", "==", rucEmpresa),
                where("fechaJornada", ">=", fechaInicio),
                where("fechaJornada", "<=", fechaFin)
            );

            const marcacionesSnapshot = await getDocs(marcacionesQuery);
            let marcaciones = [];

            marcacionesSnapshot.forEach((doc) => {
                const data = doc.data();
                const categoria = getCategoriaRegistro(data);
                const esTipoValido = tipoRegistro === "todos" || categoria === tipoRegistro;

                if (esTipoValido) {
                    console.log("📍 Marcación cargada - Ruta exacta en Firestore:", doc.ref.path, data);
                    // Solo filtramos por empresa de transporte para registros de transporte.
                    if (empresaSeleccionada !== "todos" && categoria === "bus") {
                        const busId = data.busId || data.metadata?.busId;
                        let bus = buses.find(b => b.id === busId);
                        
                        if (!bus) {
                            const placaBusqueda = data.busPlaca || data.metadata?.busPlaca;
                            if (placaBusqueda) {
                                bus = buses.find(b => b.placa === placaBusqueda);
                            }
                        }

                        if (bus?.empresaTransporte === empresaSeleccionada) {
                            marcaciones.push({ id: doc.id, path: doc.ref.path, ...data });
                        }
                    } else {
                        marcaciones.push({ id: doc.id, path: doc.ref.path, ...data });
                    }
                }
            });

            // Auto-completar datos de usuarios de acuerdo a DNI, Legajo y Legajo2 al cargar
            const marcacionesEnriquecidas = marcaciones.map((record) => {
                const empleado = encontrarEmpleado(record);
                if (!empleado) return record;

                const { nombre, apellidos } = resolverNombreUsuario(empleado);
                const dniReal = getString(empleado.dni);
                const legajoReal = getString(empleado.legajo);
                const legajo2Real = getString(empleado.legajo2);
                const cecoReal = getString(empleado.cecoNombre || empleado.ceco);
                const cargoReal = getString(empleado.cargo);

                return {
                    ...record,
                    nombre: record.nombre || nombre,
                    apellidos: record.apellidos || apellidos,
                    dni: dniReal || record.dni,
                    legajo: record.legajo || legajoReal,
                    legajo2: record.legajo2 || legajo2Real,
                    ceco: record.ceco || cecoReal,
                    cargo: record.cargo || cargoReal,
                };
            });

            setMarcacionesData(marcacionesEnriquecidas);
            vincularMasivoSilencioso(marcacionesEnriquecidas);

            if (marcaciones.length === 0) {
                setError("No se encontraron registros para los filtros seleccionados.");
            }
        } catch (err) {
            console.error("Error al cargar marcaciones:", err);
            if (handleQuotaExhaustedAlert(err)) {
                setError("Cuota diaria de Firestore excedida (Quota Exceeded). Intente más tarde.");
            } else {
                setError("Error al cargar las marcaciones. Por favor, intente nuevamente.");
            }
        } finally {
            setLoading(false);
        }
    };

    const toggleSeleccionRegistro = (recordPath) => {
        setRegistrosSeleccionados((prev) => {
            const copy = new Set(prev);
            if (copy.has(recordPath)) {
                copy.delete(recordPath);
            } else {
                copy.add(recordPath);
            }
            return copy;
        });
    };

    const toggleSeleccionarTodos = () => {
        if (registrosSeleccionados.size === datosFiltrados.length && datosFiltrados.length > 0) {
            setRegistrosSeleccionados(new Set());
        } else {
            const todosPaths = new Set(datosFiltrados.map((m) => m.path).filter(Boolean));
            setRegistrosSeleccionados(todosPaths);
        }
    };

    const ejecutarBorradoEnLotes = async (listaRegistros, tituloExito) => {
        setEliminandoMasivo(true);
        try {
            const total = listaRegistros.length;
            const BATCH_SIZE = 450;

            for (let i = 0; i < total; i += BATCH_SIZE) {
                const chunk = listaRegistros.slice(i, i + BATCH_SIZE);
                const batch = writeBatch(db);
                chunk.forEach((rec) => {
                    if (rec.path) {
                        batch.delete(doc(db, rec.path));
                    }
                });
                await batch.commit();
            }

            const pathsEliminados = new Set(listaRegistros.map((r) => r.path));
            setMarcacionesData((prev) => prev.filter((m) => !pathsEliminados.has(m.path)));
            setRegistrosSeleccionados((prev) => {
                const copy = new Set(prev);
                pathsEliminados.forEach((p) => copy.delete(p));
                return copy;
            });

            await Swal.fire("Éxito", tituloExito, "success");
        } catch (error) {
            console.error("Error en borrado masivo:", error);
            await Swal.fire("Error", "Ocurrió un error al eliminar los registros.", "error");
        } finally {
            setEliminandoMasivo(false);
        }
    };

    const handleEliminarSeleccionados = async () => {
        if (registrosSeleccionados.size === 0) return;
        const seleccionados = marcacionesData.filter((m) => registrosSeleccionados.has(m.path));

        const result = await Swal.fire({
            title: `¿Eliminar ${seleccionados.length} registros seleccionados?`,
            text: "Esta acción eliminará de forma permanente únicamente los registros marcados.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Sí, eliminar seleccionados",
            cancelButtonText: "Cancelar"
        });

        if (result.isConfirmed) {
            await ejecutarBorradoEnLotes(
                seleccionados,
                `Se eliminaron ${seleccionados.length} registros seleccionados correctamente.`
            );
        }
    };

    const handleEliminarTodosFiltrados = async () => {
        if (marcacionesData.length === 0) return;

        const catLabel = tipoRegistro === "todos" ? "todos los tipos" : `tipo "${tipoRegistro}"`;

        const result = await Swal.fire({
            title: `¿Eliminar los ${marcacionesData.length} registros cargados?`,
            html: `Se eliminarán <b>${marcacionesData.length}</b> marcaciones listadas (${catLabel}).<br><br><span class="text-danger"><b>Esta acción eliminará de forma permanente únicamente los registros que coinciden con los filtros actuales.</b></span>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: `Sí, eliminar ${marcacionesData.length} registros`,
            cancelButtonText: "Cancelar"
        });

        if (result.isConfirmed) {
            await ejecutarBorradoEnLotes(
                marcacionesData,
                `Se eliminaron ${marcacionesData.length} registros filtrados correctamente.`
            );
        }
    };

    const handleEliminar = async (record) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: "Esta acción eliminará el registro de asistencia permanentemente.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, record.path));
                setMarcacionesData(prev => prev.filter(m => m.id !== record.id));
                setRegistrosSeleccionados(prev => {
                    const copy = new Set(prev);
                    copy.delete(record.path);
                    return copy;
                });
                Swal.fire(
                    'Eliminado',
                    'El registro ha sido eliminado correctamente.',
                    'success'
                );
            } catch (error) {
                console.error("Error al eliminar registro:", error);
                Swal.fire(
                    'Error',
                    'No se pudo eliminar el registro. Intente nuevamente.',
                    'error'
                );
            }
        }
    };

    const getString = (value) => String(value || "").trim();

    const resolverNombreUsuario = (userData = {}) => {
        const nombre = getString(userData.name || userData.nombres);
        const apellidos = getString(
            userData.apellidos ||
            `${getString(userData.apellidoPaterno)} ${getString(userData.apellidoMaterno)}`.trim()
        );
        return { nombre, apellidos };
    };

    const encontrarEmpleado = (record, customList = null) => {
        if (!record) return null;
        const listToUse = customList || employeeList;
        if (!Array.isArray(listToUse) || listToUse.length === 0) return null;

        const candDni = getString(record.dni || record.metadata?.dni || record.metadata?.userDni);
        const candLegajo = getString(record.legajo || record.metadata?.legajo);
        const candLegajo2 = getString(record.legajo2 || record.metadata?.legajo2);

        const candidatos = [candDni, candLegajo, candLegajo2].filter(
            (v) => v && v.toLowerCase() !== "system"
        );
        if (candidatos.length === 0) return null;

        const matchCode = (cand, empCode) => {
            if (!empCode || !cand) return false;
            if (empCode === cand) return true;
            const cNorm = cand.replace(/\D/g, "");
            const eNorm = empCode.replace(/\D/g, "");
            if (!cNorm || !eNorm) return false;
            if (cNorm === eNorm) return true;
            if (cNorm.padStart(8, "0") === eNorm.padStart(8, "0")) return true;
            if (cNorm.padStart(10, "0") === eNorm.padStart(10, "0")) return true;
            if (cNorm.replace(/^0+/, "") === eNorm.replace(/^0+/, "")) return true;
            return false;
        };

        return listToUse.find((emp) => {
            const empDni = getString(emp.dni);
            const empLegajo = getString(emp.legajo);
            const empLegajo2 = getString(emp.legajo2);

            return candidatos.some((cand) => (
                matchCode(cand, empDni) ||
                matchCode(cand, empLegajo) ||
                matchCode(cand, empLegajo2)
            ));
        });
    };

    const reintentarVinculacionRegistro = async (record) => {
        const rowKey = `${record.path}::${record.id}`;
        setActualizandoRegistros((prev) => new Set(prev).add(rowKey));
        try {
            establecerOrigenConsumo("asistencia:ingresos");
            let legajoIntento = getString(record.legajo || record.metadata?.legajo || record.dni);
            if (!legajoIntento || legajoIntento.toLowerCase() === "system") {
                const { value: dniIngresado } = await Swal.fire({
                    title: "Vincular Registro de Asistencia",
                    text: "Este registro figura como 'system' o sin DNI. Ingrese el DNI o Legajo del trabajador a vincular (Ej: 10250362):",
                    input: "text",
                    inputPlaceholder: "10250362",
                    showCancelButton: true,
                    confirmButtonText: "Vincular",
                    cancelButtonText: "Cancelar",
                    inputValidator: (val) => {
                        if (!val || !val.trim()) return "Debe ingresar un DNI o Legajo";
                    }
                });

                if (!dniIngresado) return;
                legajoIntento = dniIngresado.trim();
            }

            const usersRef = collection(db, "users");
            const rucStr = String(rucEmpresa).trim();

            // Optimización: Buscar primero en memoria en employeeList con normalización
            let userData = encontrarEmpleado({ dni: legajoIntento, legajo: legajoIntento, legajo2: legajoIntento });

            if (!userData) {
                let userSnap = await getDocs(
                    query(
                        usersRef,
                        where("rucEmpresa", "==", rucStr),
                        where("dni", "==", legajoIntento)
                    )
                );

                if (userSnap.empty && !isNaN(legajoIntento)) {
                    userSnap = await getDocs(
                        query(
                            usersRef,
                            where("rucEmpresa", "==", rucStr),
                            where("dni", "==", parseInt(legajoIntento, 10))
                        )
                    );
                }

                if (userSnap.empty) {
                    userSnap = await getDocs(
                        query(
                            usersRef,
                            where("rucEmpresa", "==", rucStr),
                            where("legajo", "==", legajoIntento)
                        )
                    );
                }

                if (userSnap.empty) {
                    userSnap = await getDocs(
                        query(
                            usersRef,
                            where("rucEmpresa", "==", rucStr),
                            where("legajo2", "==", legajoIntento)
                        )
                    );
                }

                if (!userSnap.empty) {
                    userData = userSnap.docs[0].data() || {};
                }
            }

            if (!userData) {
                await Swal.fire("No encontrado", `No se encontró usuario en la empresa con DNI, Legajo o Legajo 2: ${legajoIntento}`, "info");
                return;
            }
            const dniReal = getString(userData.dni || userData.id);
            if (!dniReal) {
                await Swal.fire("Sin DNI", "El usuario encontrado no tiene DNI válido.", "warning");
                return;
            }

            const { nombre, apellidos } = resolverNombreUsuario(userData);
            const dataActualizada = {
                ...record,
                dni: dniReal,
                nombre,
                apellidos,
                legajo: getString(userData.legajo || legajoIntento),
                ceco: getString(userData.cecoNombre || record.ceco),
                cargo: getString(userData.cargo || record.cargo),
                updatedAt: new Date().toISOString(),
                metadata: {
                    ...(record.metadata || {}),
                    legajo: getString(userData.legajo || legajoIntento),
                    relinkedByAdmin: true,
                    relinkedAt: new Date().toISOString(),
                },
            };
            delete dataActualizada.id;
            delete dataActualizada.path;

            const destinoRef = doc(db, `asistencia/${dniReal}/registros`, record.id);
            await setDoc(destinoRef, dataActualizada, { merge: true });

            if (record.path !== destinoRef.path) {
                await deleteDoc(doc(db, record.path));
            }

            setMarcacionesData((prev) =>
                prev.map((item) =>
                    item.id === record.id && item.path === record.path
                        ? { ...item, ...dataActualizada, path: destinoRef.path, dni: dniReal }
                        : item
                )
            );

            await Swal.fire("Actualizado", `Registro vinculado exitosamente a ${nombre} ${apellidos} (DNI ${dniReal}).`, "success");
        } catch (error) {
            console.error("Error al actualizar vinculación del registro:", error);
            await Swal.fire("Error", "No se pudo actualizar el registro.", "error");
        } finally {
            setActualizandoRegistros((prev) => {
                const copy = new Set(prev);
                copy.delete(rowKey);
                return copy;
            });
        }
    };

    const actualizarMasivoIncompletos = async () => {
        // Filtrar registros incompletos o sin vincular de la lista filtrada actual
        const incompletos = datosFiltrados.filter((record) => {
            const categoria = getCategoriaRegistro(record);
            const isTrackingGPS = record.tipo === "TRACKING" || record.metadata?.origen === "background_service" || categoria === "tracking";
            if (isTrackingGPS) return false;

            const empleado = encontrarEmpleado(record);
            const dniRegistro = getString(record.dni);
            const dniSoloDigitos = dniRegistro.replace(/\D/g, "");
            const esSystemOUnlinked = !dniRegistro || dniRegistro.toLowerCase() === "system" || (!record.nombre && !record.apellidos && !empleado);
            return esSystemOUnlinked || dniSoloDigitos.length > 9 || !empleado;
        });

        if (incompletos.length === 0) {
            await Swal.fire("Sin incompletos", "No se encontraron registros incompletos o sin vincular en la lista actual.", "info");
            return;
        }

        const result = await Swal.fire({
            title: "Actualización Masiva de Registros",
            html: `Se encontraron <b>${incompletos.length}</b> registro(s) incompletos o sin vincular.<br/><br/>El sistema intentará buscar y vincular automáticamente sus Nombres, Apellidos, CECO y Cargo en la base de datos de usuarios.<br/><br/>¿Desea continuar?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: `Sí, vincular ${incompletos.length} registros`,
            cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return;

        setActualizandoMasivo(true);
        let exitosos = 0;
        let requerioManual = 0;
        let fallidos = 0;

        const usersRef = collection(db, "users");
        const rucStr = String(rucEmpresa).trim();

        // Cargar mapa de usuarios para procesamiento ultrarrápido
        let usersMapByDni = new Map();
        let usersMapByLegajo = new Map();
        try {
            establecerOrigenConsumo("asistencia:ingresos");
            const usersSnap = await getDocs(query(usersRef, where("rucEmpresa", "==", rucStr)));
            usersSnap.forEach((uDoc) => {
                const uData = uDoc.data();
                const registerInMap = (map, val, data) => {
                    if (!val) return;
                    const vStr = getString(val);
                    if (!vStr) return;
                    map.set(vStr, data);
                    const vNorm = vStr.replace(/\D/g, "");
                    if (vNorm) {
                        map.set(vNorm, data);
                        map.set(vNorm.padStart(8, "0"), data);
                        map.set(vNorm.padStart(10, "0"), data);
                        map.set(vNorm.replace(/^0+/, ""), data);
                    }
                };

                if (uData.dni) registerInMap(usersMapByDni, uData.dni, uData);
                if (uData.legajo) registerInMap(usersMapByLegajo, uData.legajo, uData);
                if (uData.legajo2) registerInMap(usersMapByLegajo, uData.legajo2, uData);
            });
        } catch (e) {
            console.warn("Error pre-cargando usuarios para actualización masiva:", e);
        }

        let marcacionesCopia = [...marcacionesData];

        for (const record of incompletos) {
            try {
                let legajoIntento = getString(record.legajo || record.metadata?.legajo || record.metadata?.userDni || record.dni);
                if (legajoIntento.toLowerCase() === "system") {
                    legajoIntento = getString(record.metadata?.legajo || record.metadata?.userDni);
                }

                let userData = null;
                if (legajoIntento) {
                    const legClean = legajoIntento.replace(/\D/g, "");
                    userData = usersMapByDni.get(legajoIntento) ||
                        usersMapByDni.get(legClean) ||
                        usersMapByDni.get(legClean.padStart(8, "0")) ||
                        usersMapByDni.get(legClean.padStart(10, "0")) ||
                        usersMapByDni.get(legClean.replace(/^0+/, "")) ||
                        usersMapByLegajo.get(legajoIntento) ||
                        usersMapByLegajo.get(legClean) ||
                        usersMapByLegajo.get(legClean.padStart(8, "0")) ||
                        usersMapByLegajo.get(legClean.padStart(10, "0")) ||
                        usersMapByLegajo.get(legClean.replace(/^0+/, ""));
                }

                if (!userData) {
                    userData = encontrarEmpleado(record);
                }

                if (!userData) {
                    requerioManual++;
                    continue;
                }

                const dniReal = getString(userData.dni || userData.id);
                if (!dniReal) {
                    fallidos++;
                    continue;
                }

                const { nombre, apellidos } = resolverNombreUsuario(userData);
                const dataActualizada = {
                    ...record,
                    dni: dniReal,
                    nombre,
                    apellidos,
                    legajo: getString(userData.legajo || legajoIntento),
                    ceco: getString(userData.cecoNombre || record.ceco),
                    cargo: getString(userData.cargo || record.cargo),
                    updatedAt: new Date().toISOString(),
                    metadata: {
                        ...(record.metadata || {}),
                        legajo: getString(userData.legajo || legajoIntento),
                        relinkedByAdmin: true,
                        relinkedAt: new Date().toISOString(),
                    },
                };
                delete dataActualizada.id;
                delete dataActualizada.path;

                const destinoRef = doc(db, `asistencia/${dniReal}/registros`, record.id);
                await setDoc(destinoRef, dataActualizada, { merge: true });

                if (record.path !== destinoRef.path) {
                    await deleteDoc(doc(db, record.path));
                }

                marcacionesCopia = marcacionesCopia.map((item) =>
                    item.id === record.id && item.path === record.path
                        ? { ...item, ...dataActualizada, path: destinoRef.path, dni: dniReal }
                        : item
                );

                exitosos++;
            } catch (errRecord) {
                console.error("Error al actualizar registro masivo:", record, errRecord);
                fallidos++;
            }
        }

        setMarcacionesData(marcacionesCopia);
        setActualizandoMasivo(false);

        await Swal.fire({
            title: "Actualización Masiva Finalizada",
            html: `
                <div style="text-align: left;">
                    <p><b>Resumen del proceso:</b></p>
                    <ul>
                        <li><b>Registros vinculados exitosamente:</b> ${exitosos}</li>
                        ${requerioManual > 0 ? `<li><b>Requieren DNI manual (sin DNI previo):</b> ${requerioManual}</li>` : ""}
                        ${fallidos > 0 ? `<li><b>Errores:</b> ${fallidos}</li>` : ""}
                    </ul>
                </div>
            `,
            icon: exitosos > 0 ? "success" : "info",
        });
    };

    const vincularMasivoSilencioso = async (listaMarcaciones) => {
        try {
            const incompletos = listaMarcaciones.filter((record) => {
                const categoria = getCategoriaRegistro(record);
                const isTrackingGPS = record.tipo === "TRACKING" || record.metadata?.origen === "background_service" || categoria === "tracking";
                if (isTrackingGPS) return false;

                const empleado = encontrarEmpleado(record);
                const dniRegistro = getString(record.dni);
                const dniSoloDigitos = dniRegistro.replace(/\D/g, "");
                const esSystemOUnlinked = !dniRegistro || dniRegistro.toLowerCase() === "system" || (!record.nombre && !record.apellidos && !empleado);
                return (esSystemOUnlinked || dniSoloDigitos.length > 9 || !record.nombre || !record.apellidos) && empleado;
            });

            if (incompletos.length === 0) return;

            let marcacionesCopia = [...listaMarcaciones];
            let huboCambios = false;

            for (const record of incompletos) {
                const empleado = encontrarEmpleado(record);
                if (!empleado) continue;

                const dniReal = getString(empleado.dni || record.dni);
                if (!dniReal || dniReal.toLowerCase() === "system") continue;

                const { nombre, apellidos } = resolverNombreUsuario(empleado);
                const dataActualizada = {
                    ...record,
                    dni: dniReal,
                    nombre: record.nombre || nombre,
                    apellidos: record.apellidos || apellidos,
                    legajo: getString(empleado.legajo || record.legajo),
                    legajo2: getString(empleado.legajo2 || record.legajo2),
                    ceco: getString(empleado.cecoNombre || record.ceco),
                    cargo: getString(empleado.cargo || record.cargo),
                    updatedAt: new Date().toISOString(),
                    metadata: {
                        ...(record.metadata || {}),
                        legajo: getString(empleado.legajo || record.legajo),
                        legajo2: getString(empleado.legajo2 || record.legajo2),
                        relinkedByAdmin: true,
                        relinkedAt: new Date().toISOString(),
                    },
                };
                delete dataActualizada.id;
                delete dataActualizada.path;

                const destinoRef = doc(db, `asistencia/${dniReal}/registros`, record.id);
                await setDoc(destinoRef, dataActualizada, { merge: true });

                if (record.path !== destinoRef.path) {
                    await deleteDoc(doc(db, record.path));
                }

                marcacionesCopia = marcacionesCopia.map((item) =>
                    item.id === record.id && item.path === record.path
                        ? { ...item, ...dataActualizada, path: destinoRef.path, dni: dniReal }
                        : item
                );
                huboCambios = true;
            }

            if (huboCambios) {
                setMarcacionesData(marcacionesCopia);
            }
        } catch (err) {
            console.warn("Error en actualización silenciosa de incompletos:", err);
        }
    };

    // Helper para obtener nombre del conductor (con caché)
    const getDriverName = async (dni) => {
        if (!dni) return "No registrado";
        if (driversCache[dni]) return driversCache[dni];

        try {
            establecerOrigenConsumo("asistencia:ingresos");
            const usersRef = collection(db, "users");
            const q = query(usersRef, where("dni", "==", dni));
            const snap = await getDocs(q);
            if (!snap.empty) {
                const data = snap.docs[0].data();
                const name = `${data.name || ""} ${data.apellidos || ""}`.trim();
                setDriversCache(prev => ({ ...prev, [dni]: name }));
                return name;
            } else {
                setDriversCache(prev => ({ ...prev, [dni]: `DNI: ${dni}` }));
                return `DNI: ${dni}`;
            }
        } catch (error) {
            console.error("Error fetching driver:", error);
            return `Error: ${dni}`;
        }
    };

    // Efecto para cargar conductores de las marcaciones cargadas
    useEffect(() => {
        const loadDrivers = async () => {
            establecerOrigenConsumo("asistencia:ingresos");
            const uniqueDrivers = [...new Set(marcacionesData.map(m => m.dniConductor || m.metadata?.driverDni).filter(Boolean))];
            const newDrivers = {};

            for (const dni of uniqueDrivers) {
                if (!driversCache[dni]) {
                    // Fetch logic duplicated here or ensure helper updates state correctly
                    // For simplicity, we'll iterate and update cache in state
                    try {
                        const usersRef = collection(db, "users");
                        const q = query(usersRef, where("dni", "==", dni));
                        const snap = await getDocs(q);
                        if (!snap.empty) {
                            const data = snap.docs[0].data();
                            newDrivers[dni] = `${data.name || ""} ${data.apellidos || ""}`.trim();
                        } else {
                            newDrivers[dni] = `DNI: ${dni}`;
                        }
                    } catch (e) {
                        console.error(e);
                    }
                }
            }

            if (Object.keys(newDrivers).length > 0) {
                setDriversCache(prev => ({ ...prev, ...newDrivers }));
            }
        };

        if (marcacionesData.length > 0) {
            loadDrivers();
        }
    }, [marcacionesData]);

    // Función para exportar a Excel
    const exportarAExcel = () => {
        if (marcacionesData.length === 0) {
            setError("No hay datos para exportar.");
            return;
        }

        try {
            setExportando(true);

            // Preparar datos para Excel
            const datosExcel = marcacionesData.map(record => {
                // Buscar nombre del empleado
                const empleado = encontrarEmpleado(record);

                // Buscar datos de Bus/Ruta si existen
                const busId = record.busId || record.metadata?.busId;
                const rutaId = record.rutaId || record.metadata?.rutaId;

                // Intentar buscar bus por ID
                let bus = buses.find(b => b.id === busId);

                // Si no se encuentra por ID, intentar buscar por Placa
                if (!bus) {
                    const placaBusqueda = record.busPlaca || record.metadata?.busPlaca;
                    if (placaBusqueda) {
                        bus = buses.find(b => b.placa === placaBusqueda);
                    }
                }
                bus = bus || {};

                const ruta = rutas.find(r => r.id === rutaId) || {};

                const lat = record.ubicacion?.latitud || record.latitud;
                const lng = record.ubicacion?.longitud || record.longitud;
                const validacion = validarPuntoEnRuta(lat, lng, ruta);

                // Obtener datos directos de metadata si existen
                const busPlaca = record.busPlaca || record.metadata?.busPlaca || bus.placa || "";

                // Nombre Conductor
                const dniConductor = record.dniConductor || record.metadata?.driverDni;
                const conductor = driversCache[dniConductor] || "No registrado";

                // Priorizar el nombre guardado en el registro, si existe
                let nombrePasajero = '';
                let apellidoPasajero = '';

                if (record.tipo === "TRACKING" || getCategoriaRegistro(record) === "tracking") {
                    nombrePasajero = record.metadata?.driverRealName ? `Chofer: ${record.metadata.driverRealName}` : 'Rastreo GPS Bus';
                    apellidoPasajero = '(Ubicación en 2do plano)';
                } else if (record.nombre || record.apellidos) {
                    nombrePasajero = record.nombre || '';
                    apellidoPasajero = record.apellidos || '';
                } else if (empleado) {
                    nombrePasajero = empleado.nombres || empleado.name || '';
                    apellidoPasajero = `${empleado.apellidoPaterno || empleado.apellidos || ''} ${empleado.apellidoMaterno || ''}`.trim();
                }

                // Buscar Código de CECO
                const cecoData = cecosList.find(c =>
                    c.id === empleado?.cecoId || (c.nombre && c.nombre === empleado?.cecoNombre)
                );
                const cecoCodigo = cecoData?.codigo || "-";

                return {
                    'Fecha': record.fechaJornada || (record.fechaHoraPeru ? record.fechaHoraPeru.split(" ")[0] : ""),
                    'Hora': record.fechaMarcacion || (record.timestamp ? format(record.timestamp.toDate(), 'HH:mm') : ''),
                    'Tipo': getCategoriaLabel(getCategoriaRegistro(record)),
                    'Tipo Original': record.tipo || "",
                    'Empresa Transporte': bus.empresaTransporte || "",
                    'Placa Bus': busPlaca,
                    'Ruta': ruta.nombre || "No Asignada",
                    'DNI Conductor': dniConductor || "",
                    'DNI Pasajero': record.dni || '',
                    'Nombre Pasajero': nombrePasajero,
                    'Apellidos Pasajero': apellidoPasajero,
                    'CECO': empleado?.cecoNombre || '',
                    'Cód. CECO': cecoCodigo,
                    'Latitud': record.ubicacion?.latitud || record.latitud || '',
                    'Longitud': record.ubicacion?.longitud || record.longitud || '',
                    'Google Maps': (record.ubicacion?.latitud || record.latitud) && (record.ubicacion?.longitud || record.longitud)
                        ? `https://www.google.com/maps?q=${record.ubicacion?.latitud || record.latitud},${record.ubicacion?.longitud || record.longitud}`
                        : '',
                    'Validación Ruta': validacion.estado,
                    'Desviación (m)': validacion.desviacion ? Math.round(validacion.desviacion) : ''
                };
            });

            // Crear libro de Excel
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(datosExcel);

            // Configurar ancho de columnas
            const colWidths = [
                { wch: 12 }, // Fecha
                { wch: 10 }, // Hora
                { wch: 15 }, // Tipo
                { wch: 20 }, // Tipo Original
                { wch: 25 }, // Empresa
                { wch: 15 }, // Placa
                { wch: 20 }, // Ruta
                { wch: 15 }, // DNI Cond
                { wch: 15 }, // DNI Pasajero
                { wch: 20 }, // Nombre Pasajero
                { wch: 20 }, // Apellido Pasajero
                { wch: 20 }, // CECO
                { wch: 15 }, // Cód. CECO
                { wch: 15 }, // Latitud
                { wch: 15 }, // Longitud
                { wch: 40 }, // Google Maps
                { wch: 15 }, // Validación
                { wch: 15 } // Desviación
            ];
            ws['!cols'] = colWidths;

            // Agregar hoja al libro
            XLSX.utils.book_append_sheet(wb, ws, `Ingresos`);

            // Generar nombre del archivo
            // Generar nombre del archivo
            const nombreArchivo = `reporte_ingresos_${rucEmpresa}_${fechaInicio}_${fechaFin}.xlsx`;

            // Descargar archivo
            XLSX.writeFile(wb, nombreArchivo);

        } catch (err) {
            console.error("Error al exportar a Excel:", err);
            setError("Error al generar el archivo Excel.");
        } finally {
            setExportando(false);
        }
    };

    const exportarFormatoMiBoleta = () => {
        if (marcacionesData.length === 0) {
            setError("No hay datos para exportar.");
            return;
        }

        try {
            setExportando(true);
            setError(null);

            const safeStr = (value) => (value == null ? "" : String(value).trim());
            const normalizarTexto = (value) =>
                safeStr(value)
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .toUpperCase();
            const tipoOrden = (tipo = "") => {
                const tipoLower = tipo.toLowerCase();
                if (tipoLower.includes("ingreso")) return 1;
                if (tipoLower.includes("salida")) return 2;
                return 3;
            };
            // Las tarifas de ruta son soles enteros: se mantienen enteras para que los totales
            // (tarifa x cantidad de viajes) nunca arrastren centavos.
            const round0 = (value) => Math.round(Number(value) || 0);
            // Importe con 2 decimales y formato 0.00 (Excel muestra 1700.00, nunca 1700 ni 1700.01).
            // El redondeo a 2 decimales aquí limpia el ruido de coma flotante (38.5700000004).
            const celdaMoneda = (valor) => ({ t: "n", v: Math.round((Number(valor) || 0) * 100) / 100, z: "0.00" });
            // Los repartos se acumulan en CENTAVOS enteros: así no hay deriva de centavos y los
            // subtotales/totales caen siempre en .00. Se convierte a soles solo al escribir la celda.
            const aSoles = (centavos) => Math.round(Number(centavos) || 0) / 100;
            /**
             * Reparte `totalCents` (CENTAVOS enteros) entre varias partes según su cantidad de
             * unidades, método del mayor residuo: la suma de las partes es EXACTAMENTE el total.
             * Cada CECO lleva sus céntimos exactos (38.57 + 25.71 + 25.72) y aun así el subtotal del
             * viaje y los totales del reporte cierran en .00, sin el descuadre de redondear cada parte.
             */
            const repartirEnCentavos = (totalCents, partes, totalUnidades) => {
                const base = Math.round(Number(totalCents) || 0);
                const resultado = {};
                if (!partes || partes.length === 0) return resultado;

                const unidades = Number(totalUnidades) || 0;
                const exactos = partes.map((parte) => {
                    const cantidad = Number(parte.cantidad) || 0;
                    const montoExacto = unidades > 0 ? (base * cantidad) / unidades : 0;
                    const piso = Math.floor(montoExacto);
                    return { ...parte, cantidad, piso, residuo: montoExacto - piso };
                });

                const restante = base - exactos.reduce((acc, parte) => acc + parte.piso, 0);
                const orden = [...exactos].sort((a, b) => {
                    if (restante >= 0) {
                        if (b.residuo !== a.residuo) return b.residuo - a.residuo;
                    } else if (a.residuo !== b.residuo) {
                        return a.residuo - b.residuo;
                    }
                    return String(a.ceco).localeCompare(String(b.ceco));
                });

                orden.forEach((parte, index) => {
                    const ajuste = index < Math.abs(restante) ? (restante >= 0 ? 1 : -1) : 0;
                    resultado[parte.ceco] = parte.piso + ajuste;
                });

                return resultado;
            };
            const resolverTipoViaje = (record = {}) => {
                const tipoTexto = normalizarTexto(
                    record.tipo ||
                    record.metadata?.tipoMarcacion ||
                    record.metadata?.tipoMovimiento ||
                    record.metadata?.movimiento ||
                    ""
                );

                if (tipoTexto.includes("SALIDA")) return "salida";
                if (tipoTexto.includes("INGRESO") || tipoTexto.includes("ENTRADA")) return "ingreso";
                return "otro";
            };

            const tarifasPorRutaNombre = new Map();
            rutas.forEach((ruta) => {
                const nombreRuta = safeStr(ruta?.nombre);
                if (!nombreRuta) return;
                tarifasPorRutaNombre.set(normalizarTexto(nombreRuta), {
                    ingreso: Number(ruta?.tarifaIngreso ?? ruta?.tarifa ?? 0) || 0,
                    salida: Number(ruta?.tarifaSalida ?? 0) || 0,
                    legacy: Number(ruta?.tarifa ?? 0) || 0,
                });
            });

            const ordenarPorFecha = (a, b) => {
                const fechaA = new Date(`${a.fecha}T00:00:00`);
                const fechaB = new Date(`${b.fecha}T00:00:00`);
                return fechaA - fechaB;
            };

            const registrosNormalizados = marcacionesData.map((record) => {
                const empleado = encontrarEmpleado(record);
                const busId = record.busId || record.metadata?.busId;
                const rutaId = record.rutaId || record.metadata?.rutaId;

                let bus = buses.find((b) => b.id === busId);
                if (!bus) {
                    const placaBusqueda = record.busPlaca || record.metadata?.busPlaca;
                    if (placaBusqueda) {
                        bus = buses.find((b) => b.placa === placaBusqueda);
                    }
                }
                bus = bus || {};

                const ruta = rutas.find((r) => r.id === rutaId) || {};
                const fecha = record.fechaJornada || (record.fechaHoraPeru ? record.fechaHoraPeru.split(" ")[0] : "");
                const tipo = safeStr(record.tipo);
                const empresaTransporte = safeStr(record.empresaTransporte || record.metadata?.empresaTransporte || bus.empresaTransporte || "No especificado");
                const rutaNombre = safeStr(record.rutaNombre || record.metadata?.rutaNombre || ruta.nombre || "No Asignada");
                const placaBus = safeStr(record.busPlaca || record.metadata?.busPlaca || bus.placa || "");
                const dniConductor = safeStr(record.dniConductor || record.metadata?.driverDni || "");
                const ceco = safeStr(record.ceco || record.metadata?.ceco || empleado?.cecoNombre || empleado?.ceco || "SIN CECO");
                const cecoInfo = cecosList.find(c => c.id === empleado?.cecoId || (c.nombre && c.nombre === ceco));
                const cecoCodigo = cecoInfo?.codigo || "-";

                const nombrePasajero = safeStr(record.nombre || empleado?.nombres || "");
                const apellidosPasajero = safeStr(record.apellidos || (empleado ? `${empleado.apellidoPaterno || ""} ${empleado.apellidoMaterno || ""}`.trim() : ""));
                const capacidad = Number(record.capacidad || record.metadata?.capacidad || bus.capacidad) || 0;
                const tipoViaje = resolverTipoViaje(record);
                const tarifaIngresoRuta = Number(ruta.tarifaIngreso ?? ruta.tarifa ?? 0) || 0;
                const tarifaSalidaRuta = Number(ruta.tarifaSalida ?? 0) || 0;
                const tarifaRegistro = Number(record.tarifa ?? record.metadata?.tarifa ?? ruta.tarifa ?? 0) || 0;

                return {
                    fecha,
                    tipo,
                    tipoViaje,
                    empresaTransporte,
                    rutaNombre,
                    placaBus,
                    dniConductor,
                    ceco,
                    nombrePasajero,
                    apellidosPasajero,
                    capacidad,
                    tarifaIngresoRuta,
                    tarifaSalidaRuta,
                    tarifaRegistro,
                    cecoCodigo,
                };
            }).filter((item) => item.fecha && item.tipo);

            const resolverTarifa = (rutaNombre, tipoViaje, tarifaRegistro = 0) => {
                const tarifasRuta = tarifasPorRutaNombre.get(normalizarTexto(rutaNombre));
                if (!tarifasRuta) return round0(Number(tarifaRegistro) || 0);

                if (tipoViaje === "salida") {
                    return round0(
                        Number(tarifasRuta.salida) ||
                        Number(tarifasRuta.ingreso) ||
                        Number(tarifasRuta.legacy) ||
                        Number(tarifaRegistro) ||
                        0
                    );
                }

                if (tipoViaje === "ingreso") {
                    return round0(
                        Number(tarifasRuta.ingreso) ||
                        Number(tarifasRuta.legacy) ||
                        Number(tarifasRuta.salida) ||
                        Number(tarifaRegistro) ||
                        0
                    );
                }

                return round0(
                    Number(tarifasRuta.legacy) ||
                    Number(tarifasRuta.ingreso) ||
                    Number(tarifasRuta.salida) ||
                    Number(tarifaRegistro) ||
                    0
                );
            };

            const viajesMap = new Map();
            registrosNormalizados.forEach((item) => {
                const viajeKey = [
                    item.empresaTransporte,
                    item.fecha,
                    item.tipo,
                    item.rutaNombre,
                    item.placaBus,
                    item.dniConductor,
                ].join("|");

                if (!viajesMap.has(viajeKey)) {
                    const tarifaTarifario = resolverTarifa(item.rutaNombre, item.tipoViaje, item.tarifaRegistro);
                    viajesMap.set(viajeKey, {
                        empresaTransporte: item.empresaTransporte,
                        fecha: item.fecha,
                        tipo: item.tipo,
                        tipoViaje: item.tipoViaje,
                        rutaNombre: item.rutaNombre,
                        placaBus: item.placaBus,
                        dniConductor: item.dniConductor,
                        capacidad: item.capacidad,
                        tarifa: round0(tarifaTarifario || item.tarifaRegistro),
                        pasajeros: [],
                    });
                }

                viajesMap.get(viajeKey).pasajeros.push({
                    ceco: item.ceco || "SIN CECO",
                    nombrePasajero: item.nombrePasajero,
                    apellidosPasajero: item.apellidosPasajero,
                });
            });

            const viajes = Array.from(viajesMap.values()).sort((a, b) => {
                const byFecha = ordenarPorFecha(a, b);
                if (byFecha !== 0) return byFecha;
                const byProveedor = a.empresaTransporte.localeCompare(b.empresaTransporte);
                if (byProveedor !== 0) return byProveedor;
                const byTipo = tipoOrden(a.tipo) - tipoOrden(b.tipo);
                if (byTipo !== 0) return byTipo;
                return a.rutaNombre.localeCompare(b.rutaNombre);
            });

            if (viajes.length === 0) {
                setError("No hay datos válidos para generar el reporte de transporte.");
                return;
            }

            const proveedorPrincipal = viajes[0].empresaTransporte || "No especificado";
            const rangoFechas = `${formatearFechaReporte(fechaInicio)} AL ${formatearFechaReporte(fechaFin)}`;

            const viajesPorProveedor = new Map();
            viajes.forEach((viaje) => {
                const key = viaje.empresaTransporte || "No especificado";
                if (!viajesPorProveedor.has(key)) {
                    viajesPorProveedor.set(key, []);
                }
                viajesPorProveedor.get(key).push(viaje);
            });

            const distribucionProveedorCeco = {};

            const sheetCostos = [
                ["REPORTE  DE COSTOS DEL TRANSPORTE"],
                ["PROVEEDOR", proveedorPrincipal],
                ["FECHA DE RUTAS", rangoFechas],
                [],
                ["Empresa Transporte", "Fecha", "Tipo", "Ruta", "Placa Bus", "DNI Conductor", "CECO", "Cód. CECO", "Cant. Pasajero", "Capacidad", "MONTO"],
            ];

            Array.from(viajesPorProveedor.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .forEach(([proveedor, viajesProveedor]) => {
                    const viajesPorFecha = new Map();
                    viajesProveedor.forEach((viaje) => {
                        if (!viajesPorFecha.has(viaje.fecha)) {
                            viajesPorFecha.set(viaje.fecha, []);
                        }
                        viajesPorFecha.get(viaje.fecha).push(viaje);
                    });

                    let totalProveedorPasajeros = 0;
                    let totalProveedorMontoCts = 0;

                    Array.from(viajesPorFecha.entries())
                        .sort(([fechaA], [fechaB]) => new Date(`${fechaA}T00:00:00`) - new Date(`${fechaB}T00:00:00`))
                        .forEach(([fecha, viajesFecha]) => {
                            const viajesOrdenados = [...viajesFecha].sort((a, b) => {
                                const byTipo = tipoOrden(a.tipo) - tipoOrden(b.tipo);
                                if (byTipo !== 0) return byTipo;
                                return a.rutaNombre.localeCompare(b.rutaNombre);
                            });

                            let totalFechaPasajeros = 0;
                            let totalFechaMontoCts = 0;

                            viajesOrdenados.forEach((viaje) => {
                                const cecoAcumulado = {};
                                const totalPasajerosViaje = viaje.pasajeros.length;
                                const tarifaViajeCts = round0(viaje.tarifa) * 100;

                                const cecoCount = {};
                                viaje.pasajeros.forEach((pasajero) => {
                                    const ceco = pasajero.ceco || "SIN CECO";
                                    cecoCount[ceco] = (cecoCount[ceco] || 0) + 1;
                                });

                                // Reparto al céntimo: cada CECO recibe su parte exacta
                                // (38.57 + 25.71 + 25.72) y la suma da la tarifa del viaje (90.00).
                                const montosPorCecoCts = repartirEnCentavos(
                                    tarifaViajeCts,
                                    Object.entries(cecoCount).map(([ceco, cantidad]) => ({ ceco, cantidad })),
                                    totalPasajerosViaje
                                );

                                Object.entries(cecoCount).forEach(([ceco, cantidad]) => {
                                    const montoCts = montosPorCecoCts[ceco] || 0;
                                    if (!cecoAcumulado[ceco]) {
                                        cecoAcumulado[ceco] = { cantidad: 0, montoCts: 0 };
                                    }
                                    cecoAcumulado[ceco].cantidad += cantidad;
                                    cecoAcumulado[ceco].montoCts += montoCts;

                                    const distKey = `${proveedor}|${ceco}`;
                                    distribucionProveedorCeco[distKey] = (distribucionProveedorCeco[distKey] || 0) + montoCts;
                                });

                                const cecoRows = Object.entries(cecoAcumulado)
                                    .sort(([cecoA], [cecoB]) => cecoA.localeCompare(cecoB));

                                cecoRows.forEach(([ceco, data], indexCeco) => {
                                    sheetCostos.push([
                                        indexCeco === 0 ? proveedor : "",
                                        indexCeco === 0 ? formatearFechaReporte(fecha) : "",
                                        indexCeco === 0 ? viaje.tipo : "",
                                        indexCeco === 0 ? viaje.rutaNombre : "",
                                        indexCeco === 0 ? viaje.placaBus : "",
                                        indexCeco === 0 ? viaje.dniConductor : "",
                                        ceco,
                                        cecosList.find(c => c.nombre && c.nombre === ceco)?.codigo || "-",
                                        data.cantidad,
                                        "",
                                        celdaMoneda(aSoles(data.montoCts)),
                                    ]);
                                });

                                sheetCostos.push([
                                    "",
                                    "",
                                    `Total ${viaje.tipo}`,
                                    viaje.rutaNombre,
                                    viaje.placaBus,
                                    viaje.dniConductor,
                                    "",
                                    "",
                                    totalPasajerosViaje,
                                    viaje.capacidad || "",
                                    celdaMoneda(aSoles(tarifaViajeCts)),
                                ]);

                                totalFechaPasajeros += totalPasajerosViaje;
                                totalFechaMontoCts += tarifaViajeCts;
                            });

                            sheetCostos.push([
                                "",
                                `Total ${formatearFechaReporte(fecha)}`,
                                "",
                                "",
                                "",
                                "",
                                "",
                                "",
                                totalFechaPasajeros,
                                "",
                                celdaMoneda(aSoles(totalFechaMontoCts)),
                            ]);

                            totalProveedorPasajeros += totalFechaPasajeros;
                            totalProveedorMontoCts += totalFechaMontoCts;
                        });

                    sheetCostos.push([`Total ${proveedor}`, "", "", "", "", "", "", "", totalProveedorPasajeros, "", celdaMoneda(aSoles(totalProveedorMontoCts))]);
                    sheetCostos.push([`Total general ${proveedor}`, "", "", "", "", "", "", "", totalProveedorPasajeros, "", celdaMoneda(aSoles(totalProveedorMontoCts))]);
                });

            const resumenRutaMap = {};
            viajes.forEach((viaje) => {
                const key = `${viaje.empresaTransporte}|${viaje.rutaNombre}|${viaje.tipoViaje}`;
                if (!resumenRutaMap[key]) {
                    resumenRutaMap[key] = {
                        empresaTransporte: viaje.empresaTransporte,
                        rutaNombre: viaje.rutaNombre,
                        tipo: viaje.tipo,
                        tipoViaje: viaje.tipoViaje,
                        cantViajes: 0,
                        tarifa: round0(viaje.tarifa),
                        costoTotal: 0,
                    };
                }
                resumenRutaMap[key].cantViajes += 1;
                resumenRutaMap[key].costoTotal += round0(viaje.tarifa);
            });

            const resumenRows = Object.values(resumenRutaMap).sort((a, b) => {
                const byProveedor = a.empresaTransporte.localeCompare(b.empresaTransporte);
                if (byProveedor !== 0) return byProveedor;
                const byTipo = tipoOrden(a.tipo) - tipoOrden(b.tipo);
                if (byTipo !== 0) return byTipo;
                return a.rutaNombre.localeCompare(b.rutaNombre);
            });

            const sheetResumen = [
                ["RESUMEN DEL SERVICIO DE TRANSPORTE"],
                ["PROVEEDOR", proveedorPrincipal],
                ["FECHA DE RUTAS", rangoFechas],
                [],
                ["Empresa Transporte", "Ruta", "Tipo", "Suma de Cant Viajes", "Tarifa", "Costo Total"],
            ];

            resumenRows.forEach((row) => {
                const excelRow = sheetResumen.length + 1;
                sheetResumen.push([
                    row.empresaTransporte,
                    row.rutaNombre,
                    row.tipo,
                    row.cantViajes,
                    { t: "n", z: "0.00", f: `ROUND(IFERROR(INDEX(TARIFA!$D:$D,MATCH(A${excelRow}&"|"&B${excelRow}&"|"&C${excelRow},TARIFA!$E:$E,0)),0),0)` },
                    { t: "n", z: "0.00", f: `ROUND(D${excelRow}*E${excelRow},0)` },
                ]);
            });

            const totalViajes = resumenRows.reduce((acc, row) => acc + row.cantViajes, 0);
            const filaTotalResumen = sheetResumen.length + 1;
            const filaInicioResumen = 6;
            const filaFinResumen = Math.max(5, filaTotalResumen - 1);
            sheetResumen.push([
                "Total general",
                "",
                "",
                totalViajes,
                "",
                { t: "n", z: "0.00", f: `ROUND(SUM(F${filaInicioResumen}:F${filaFinResumen}),0)` },
            ]);
            sheetResumen.push([], [], [], [], [], []);
            sheetResumen.push(["", "DISTRIBUCIÓN DE COSTOS - RESUMEN"]);
            sheetResumen.push(["", "Empresa Transporte", "CECO", "MONTO"]);

            const proveedorCecoRows = Object.entries(distribucionProveedorCeco)
                .map(([key, montoCts]) => {
                    const [empresaTransporte, ceco] = key.split("|");
                    return { empresaTransporte, ceco, montoCts };
                })
                .sort((a, b) => {
                    const byProveedor = a.empresaTransporte.localeCompare(b.empresaTransporte);
                    if (byProveedor !== 0) return byProveedor;
                    return a.ceco.localeCompare(b.ceco);
                });

            let proveedorActualDist = "";
            let totalProveedorDistCts = 0;
            let totalGeneralDistCts = 0;

            proveedorCecoRows.forEach((row, idx) => {
                if (proveedorActualDist && row.empresaTransporte !== proveedorActualDist) {
                    sheetResumen.push(["", `Total ${proveedorActualDist}`, "", celdaMoneda(aSoles(totalProveedorDistCts))]);
                    totalGeneralDistCts += totalProveedorDistCts;
                    totalProveedorDistCts = 0;
                }

                sheetResumen.push([
                    "",
                    row.empresaTransporte !== proveedorActualDist ? row.empresaTransporte : "",
                    row.ceco,
                    celdaMoneda(aSoles(row.montoCts)),
                ]);

                proveedorActualDist = row.empresaTransporte;
                totalProveedorDistCts += row.montoCts;

                if (idx === proveedorCecoRows.length - 1) {
                    sheetResumen.push(["", `Total ${proveedorActualDist}`, "", celdaMoneda(aSoles(totalProveedorDistCts))]);
                    totalGeneralDistCts += totalProveedorDistCts;
                }
            });

            sheetResumen.push(["", "Total general", "", celdaMoneda(aSoles(totalGeneralDistCts))]);

            const tarifaRows = resumenRows.map((row) => [
                row.empresaTransporte,
                row.rutaNombre,
                row.tipo,
                celdaMoneda(resolverTarifa(row.rutaNombre, row.tipoViaje, row.tarifa)),
                `${row.empresaTransporte}|${row.rutaNombre}|${row.tipo}`,
            ]);

            const sheetTarifa = [
                ["PROVEEDOR", "RUTA", "TIPO", "TARIFA", "CLAVE"],
                ...tarifaRows,
            ];

            const sheetDetalle = [
                ["DETALLE DEL SERVICIO DE TRANSPORTE"],
                ["PROVEEDOR", proveedorPrincipal],
                ["FECHA DE RUTAS", rangoFechas],
                [],
                ["Empresa Transporte", "Fecha", "Tipo", "Cant Viajes", "Ruta", "Placa Bus", "DNI Conductor", "CECO", "Cód. CECO", "Apellidos Pasajero", "Nombre Pasajero", "Cant. Pasajero", "Capacidad "],
            ];

            Array.from(viajesPorProveedor.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .forEach(([proveedor, viajesProveedor]) => {
                    const viajesOrdenados = [...viajesProveedor].sort((a, b) => {
                        const byFecha = ordenarPorFecha(a, b);
                        if (byFecha !== 0) return byFecha;
                        const byTipo = tipoOrden(a.tipo) - tipoOrden(b.tipo);
                        if (byTipo !== 0) return byTipo;
                        return a.rutaNombre.localeCompare(b.rutaNombre);
                    });

                    let totalProveedorDetalleViajes = 0;
                    let proveedorEscrito = false;
                    let fechaAnterior = "";

                    viajesOrdenados.forEach((viaje) => {
                        totalProveedorDetalleViajes += 1;

                        const pasajerosOrdenados = [...viaje.pasajeros].sort((a, b) => {
                            const byCeco = safeStr(a.ceco).localeCompare(safeStr(b.ceco));
                            if (byCeco !== 0) return byCeco;
                            const byApellidos = safeStr(a.apellidosPasajero).localeCompare(safeStr(b.apellidosPasajero));
                            if (byApellidos !== 0) return byApellidos;
                            return safeStr(a.nombrePasajero).localeCompare(safeStr(b.nombrePasajero));
                        });

                        pasajerosOrdenados.forEach((pasajero, idxPasajero) => {
                            const escribirFecha = idxPasajero === 0 && fechaAnterior !== viaje.fecha;
                            sheetDetalle.push([
                                idxPasajero === 0 && !proveedorEscrito ? proveedor : "",
                                escribirFecha ? formatearFechaReporte(viaje.fecha) : "",
                                idxPasajero === 0 ? viaje.tipo : "",
                                idxPasajero === 0 ? 1 : "",
                                idxPasajero === 0 ? viaje.rutaNombre : "",
                                idxPasajero === 0 ? viaje.placaBus : "",
                                idxPasajero === 0 ? viaje.dniConductor : "",
                                pasajero.ceco || "",
                                cecosList.find(c => c.id === pasajero.cecoId || (c.nombre && c.nombre === pasajero.ceco))?.codigo || "-",
                                pasajero.apellidosPasajero || "",
                                pasajero.nombrePasajero || "",
                                idxPasajero === 0 ? viaje.pasajeros.length : "",
                                idxPasajero === 0 ? (viaje.capacidad || "") : "",
                            ]);
                        });

                        proveedorEscrito = true;
                        fechaAnterior = viaje.fecha;
                    });

                    sheetDetalle.push([`Total ${proveedor}`, "", "", "", "", "", "", "", "", "", "", ""]);
                    if (totalProveedorDetalleViajes === 0) {
                        sheetDetalle.pop();
                    }
                });

            sheetDetalle.push(["Total general", "", "", "", "", "", "", "", "", "", "", ""]);

            const wb = XLSX.utils.book_new();
            const wsCostos = XLSX.utils.aoa_to_sheet(sheetCostos);
            const wsResumen = XLSX.utils.aoa_to_sheet(sheetResumen);
            const wsDetalle = XLSX.utils.aoa_to_sheet(sheetDetalle);
            const wsTarifa = XLSX.utils.aoa_to_sheet(sheetTarifa);

            wsCostos["!cols"] = [{ wch: 28 }, { wch: 16 }, { wch: 18 }, { wch: 40 }, { wch: 14 }, { wch: 14 }, { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 12 }, { wch: 16 }];
            wsResumen["!cols"] = [{ wch: 4 }, { wch: 28 }, { wch: 28 }, { wch: 18 }, { wch: 20 }, { wch: 16 }];
            wsDetalle["!cols"] = [{ wch: 28 }, { wch: 12 }, { wch: 18 }, { wch: 12 }, { wch: 36 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 26 }, { wch: 22 }, { wch: 15 }, { wch: 12 }];
            wsTarifa["!cols"] = [{ wch: 32 }, { wch: 42 }, { wch: 18 }, { wch: 12 }, { wch: 72 }];

            XLSX.utils.book_append_sheet(wb, wsCostos, "Reporte de Costos");
            XLSX.utils.book_append_sheet(wb, wsResumen, "Factura-Resumen");
            XLSX.utils.book_append_sheet(wb, wsDetalle, "Factura-Detalle");
            XLSX.utils.book_append_sheet(wb, wsTarifa, "TARIFA");

            const nombreArchivo = `reporte_transporte_miboleta_${rucEmpresa}_${fechaInicio}_${fechaFin}.xlsx`;
            XLSX.writeFile(wb, nombreArchivo);
        } catch (err) {
            console.error("Error al exportar formato Mi Boleta:", err);
            setError("Error al generar el formato Mi Boleta.");
        } finally {
            setExportando(false);
        }
    };

    // Función para obtener datos filtrados y ordenados
    const getDatosFiltradosYOrdenados = () => {
        let datosFiltrados = [...marcacionesData];

        // Aplicar búsqueda
        if (busquedaEmpleado) {
            const searchTerm = busquedaEmpleado.toLowerCase();
            datosFiltrados = datosFiltrados.filter(record => {
                const empleado = encontrarEmpleado(record);
                const nombreCompleto = empleado
                    ? `${empleado.nombres || empleado.name || ''} ${empleado.apellidoPaterno || empleado.apellidos || ''} ${empleado.apellidoMaterno || ''}`.toLowerCase()
                    : `${record.nombre || ''} ${record.apellidos || ''}`.toLowerCase();
                const dniRec = getString(record.dni || record.metadata?.dni);

                return nombreCompleto.includes(searchTerm) || (dniRec && dniRec.includes(searchTerm));
            });
        }

        // Ordenar
        datosFiltrados.sort((a, b) => {
            let valorA, valorB;

            switch (ordenarPor) {
                case "nombre":
                    const empleadoA = encontrarEmpleado(a);
                    const empleadoB = encontrarEmpleado(b);
                    valorA = empleadoA
                        ? `${empleadoA.nombres} ${empleadoA.apellidoPaterno}`.toLowerCase()
                        : `${a.nombre || ''} ${a.apellidos || ''}`.toLowerCase();
                    valorB = empleadoB
                        ? `${empleadoB.nombres} ${empleadoB.apellidoPaterno}`.toLowerCase()
                        : `${b.nombre || ''} ${b.apellidos || ''}`.toLowerCase();
                    return ordenAscendente
                        ? valorA.localeCompare(valorB)
                        : valorB.localeCompare(valorA);

                case "fecha":
                    valorA = new Date(`${a.fechaJornada}T${a.fechaMarcacion || '00:00'}`);
                    valorB = new Date(`${b.fechaJornada}T${b.fechaMarcacion || '00:00'}`);
                    return ordenAscendente ? valorA - valorB : valorB - valorA;

                case "tipo":
                    valorA = getCategoriaLabel(getCategoriaRegistro(a));
                    valorB = getCategoriaLabel(getCategoriaRegistro(b));
                    return ordenAscendente
                        ? valorA.localeCompare(valorB)
                        : valorB.localeCompare(valorA);

                default:
                    return 0;
            }
        });

        return datosFiltrados;
    };

    // Paginación
    const datosFiltrados = getDatosFiltradosYOrdenados();
    const totalPaginas = Math.ceil(datosFiltrados.length / elementosPorPagina);
    const indiceInicio = (paginaActual - 1) * elementosPorPagina;
    const indiceFin = indiceInicio + elementosPorPagina;
    const datosPaginados = datosFiltrados.slice(indiceInicio, indiceFin);

    // Componente de paginación
    const renderPaginacion = () => {
        if (totalPaginas <= 1) return null;

        const maxPaginas = 5;
        let startPage = Math.max(1, paginaActual - Math.floor(maxPaginas / 2));
        let endPage = Math.min(totalPaginas, startPage + maxPaginas - 1);

        if (endPage - startPage < maxPaginas - 1) {
            startPage = Math.max(1, endPage - maxPaginas + 1);
        }

        const pages = [];
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return (
            <Pagination size="sm" className="mb-0">
                <Pagination.First
                    onClick={() => setPaginaActual(1)}
                    disabled={paginaActual === 1}
                />
                <Pagination.Prev
                    onClick={() => setPaginaActual(paginaActual - 1)}
                    disabled={paginaActual === 1}
                />

                {startPage > 1 && (
                    <>
                        <Pagination.Item onClick={() => setPaginaActual(1)}>
                            1
                        </Pagination.Item>
                        {startPage > 2 && <Pagination.Ellipsis disabled />}
                    </>
                )}

                {pages.map((page) => (
                    <Pagination.Item
                        key={page}
                        active={page === paginaActual}
                        onClick={() => setPaginaActual(page)}
                    >
                        {page}
                    </Pagination.Item>
                ))}

                {endPage < totalPaginas && (
                    <>
                        {endPage < totalPaginas - 1 && <Pagination.Ellipsis disabled />}
                        <Pagination.Item onClick={() => setPaginaActual(totalPaginas)}>
                            {totalPaginas}
                        </Pagination.Item>
                    </>
                )}

                <Pagination.Next
                    onClick={() => setPaginaActual(paginaActual + 1)}
                    disabled={paginaActual === totalPaginas}
                />
                <Pagination.Last
                    onClick={() => setPaginaActual(totalPaginas)}
                    disabled={paginaActual === totalPaginas}
                />
            </Pagination>
        );
    };

    return (
        <>
            {/* Overlay de exportación */}
            {exportando && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(255,255,255,0.7)",
                        zIndex: 9999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                    }}
                >
                    <Spinner
                        animation="border"
                        variant="primary"
                        style={{ width: 60, height: 60 }}
                    />
                    <div className="mt-3 fs-5 fw-bold text-primary">
                        Procesando reporte, por favor espere...
                    </div>
                </div>
            )}

            <Container fluid className="py-4">
                <Card
                    className="shadow-lg border-0"
                    style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}
                >
                    {/* Header Premium con degradado verde oscuro */}
                    <Card.Header
                        className="text-white py-4 position-relative"
                        style={{
                            background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                            borderBottom: "3px solid #f59e0b",
                            borderRadius: "15px 15px 0 0",
                        }}
                    >
                        <div
                            className="position-absolute end-0 top-50 translate-middle-y me-4"
                            style={{ zIndex: 10 }}
                        >
                            <BotonManualHeader />
                        </div>
                        <div className="text-center">
                            <h2 className="fw-bold mb-1">
                                <FaFileExcel className="me-3" />
                                Reporte de Ingresos
                            </h2>
                            <p className="mb-0 opacity-75">
                                Consolidado de Comedor y Transporte | RUC: {rucEmpresa || "–"}
                            </p>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        {initialLoading ? (
                            <div className="text-center py-5">
                                <Spinner
                                    animation="border"
                                    variant="primary"
                                    style={{ width: "3rem", height: "3rem" }}
                                />
                                <p className="mt-3 text-muted">Inicializando reporte...</p>
                            </div>
                        ) : (
                            <>
                                {/* Acciones Rápidas */}
                                <Card
                                    className="border-0 mb-4 shadow-sm"
                                    style={{
                                        background: "linear-gradient(135deg,#fffcea,#fff9db)",
                                        borderRadius: 14,
                                    }}
                                >
                                    <Card.Body className="p-3 d-flex justify-content-center gap-3 flex-wrap align-items-center">
                                        <Button
                                            variant="primary"
                                            onClick={cargarMarcaciones}
                                            disabled={loading}
                                            className="d-flex align-items-center gap-2"
                                            style={{
                                                borderRadius: "10px",
                                                padding: "10px 20px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {loading ? <Spinner animation="border" size="sm" /> : <FaSyncAlt />}
                                            Generar Reporte
                                        </Button>
                                        <Button
                                            variant="success"
                                            onClick={exportarAExcel}
                                            disabled={marcacionesData.length === 0}
                                            className="d-flex align-items-center gap-2"
                                            style={{
                                                borderRadius: "10px",
                                                background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                                                border: "none",
                                                padding: "10px 20px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            <FaFileExcel size="18px" /> Exportar a Excel
                                        </Button>
                                        <Button
                                            variant="dark"
                                            onClick={exportarFormatoMiBoleta}
                                            disabled={marcacionesData.length === 0}
                                            className="d-flex align-items-center gap-2"
                                            style={{
                                                borderRadius: "10px",
                                                padding: "10px 20px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            <FaDownload size="18px" /> Exportar (3 reportes)
                                        </Button>
                                        {esSuperAdmin && (
                                            <Button
                                                variant="outline-danger"
                                                onClick={handleEliminarTodosFiltrados}
                                                disabled={marcacionesData.length === 0 || loading || eliminandoMasivo}
                                                className="d-flex align-items-center gap-2 bg-white"
                                                style={{
                                                    borderRadius: "10px",
                                                    padding: "10px 20px",
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                {eliminandoMasivo ? <Spinner animation="border" size="sm" /> : <FaTrash size="16px" />}
                                                Eliminar Registros Filtrados ({marcacionesData.length})
                                            </Button>
                                        )}
                                    </Card.Body>
                                </Card>

                                {/* Filtros */}
                                <div className="bg-white p-4 rounded-4 shadow-sm mb-4 border">
                                    <h5 className="mb-3 fw-bold text-dark d-flex align-items-center">
                                        <FaFilter className="me-2 text-primary" /> Filtros de Reporte
                                    </h5>
                                    <Form>
                                        <Row className="g-3 align-items-end">
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">Fecha Inicio</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={fechaInicio}
                                                        onChange={(e) => setFechaInicio(e.target.value)}
                                                        style={{ borderRadius: "8px" }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">Fecha Fin</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={fechaFin}
                                                        onChange={(e) => setFechaFin(e.target.value)}
                                                        style={{ borderRadius: "8px" }}
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">Tipo de Servicio</Form.Label>
                                                    <Form.Select
                                                        value={tipoRegistro}
                                                        onChange={(e) => setTipoRegistro(e.target.value)}
                                                        style={{ borderRadius: "8px" }}
                                                    >
                                                        <option value="todos">Todas las categorías</option>
                                                        <option value="bus">Transporte (Pasajeros)</option>
                                                        <option value="tracking">Tracking Bus (GPS)</option>
                                                        <option value="festividad">Festividad o Celebración</option>
                                                        <option value="capacitacion">Capacitación</option>
                                                        <option value="comedor">Comedor</option>
                                                        <option value="otro">Otro</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>

                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">Empresa Transporte</Form.Label>
                                                    <Form.Select
                                                        value={empresaSeleccionada}
                                                        onChange={(e) => setEmpresaSeleccionada(e.target.value)}
                                                        style={{ borderRadius: "8px" }}
                                                    >
                                                        <option value="todos">Todas las empresas</option>
                                                        {empresasTransporte.map((emp, idx) => (
                                                            <option key={idx} value={emp}>
                                                                {emp}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Form>
                                </div>

                                {/* Resultados */}
                                {marcacionesData.length > 0 && (
                                    <Card className="bg-white rounded-4 shadow-sm border overflow-hidden">
                                        <Card.Header className="p-3 bg-light border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
                                            <h5 className="mb-0 fw-bold">
                                                Resultados ({datosFiltrados.length})
                                                <Badge bg="info" className="ms-2">Periodo: {fechaInicio} al {fechaFin}</Badge>
                                            </h5>
                                            <div className="d-flex gap-2 align-items-center flex-wrap">
                                                <Button
                                                    variant="warning"
                                                    size="sm"
                                                    onClick={actualizarMasivoIncompletos}
                                                    disabled={actualizandoMasivo || loading}
                                                    className="d-flex align-items-center gap-1 text-dark fw-bold shadow-sm"
                                                    style={{ borderRadius: "8px" }}
                                                    title="Actualizar y vincular masivamente todos los registros incompletos o sin vincular"
                                                >
                                                    {actualizandoMasivo ? <Spinner animation="border" size="sm" /> : <FaSyncAlt size="14px" />}
                                                    Actualizar Incompletos ({
                                                        datosFiltrados.filter(record => {
                                                            const categoria = getCategoriaRegistro(record);
                                                            const isTrackingGPS = record.tipo === "TRACKING" || record.metadata?.origen === "background_service" || categoria === "tracking";
                                                            if (isTrackingGPS) return false;
                                                            const empleado = encontrarEmpleado(record);
                                                            const dniRegistro = getString(record.dni);
                                                            const dniSoloDigitos = dniRegistro.replace(/\D/g, "");
                                                            const esSystemOUnlinked = !dniRegistro || dniRegistro.toLowerCase() === "system" || (!record.nombre && !record.apellidos && !empleado);
                                                            return esSystemOUnlinked || dniSoloDigitos.length > 9 || !empleado;
                                                        }).length
                                                    })
                                                </Button>
                                                {esSuperAdmin && registrosSeleccionados.size > 0 && (
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={handleEliminarSeleccionados}
                                                        disabled={eliminandoMasivo}
                                                        className="d-flex align-items-center gap-1 me-2"
                                                        style={{ borderRadius: "8px", fontWeight: "bold" }}
                                                    >
                                                        {eliminandoMasivo ? <Spinner animation="border" size="sm" /> : <FaTrash size="14px" />}
                                                        Eliminar Seleccionados ({registrosSeleccionados.size})
                                                    </Button>
                                                )}
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Buscar por nombre o DNI..."
                                                    value={busquedaEmpleado}
                                                    onChange={(e) => {
                                                        setBusquedaEmpleado(e.target.value);
                                                        setPaginaActual(1);
                                                    }}
                                                    size="sm"
                                                    style={{ width: "250px", borderRadius: "8px" }}
                                                />
                                                <Form.Select
                                                    value={ordenarPor}
                                                    onChange={(e) => setOrdenarPor(e.target.value)}
                                                    size="sm"
                                                    style={{ width: "180px", borderRadius: "8px" }}
                                                >
                                                    <option value="fecha">Ordenar por Fecha</option>
                                                    <option value="nombre">Ordenar por Nombre</option>
                                                    <option value="tipo">Ordenar por Tipo</option>
                                                </Form.Select>
                                            </div>
                                        </Card.Header>
                                        <Card.Body className="p-0">
                                            <div className="table-responsive">
                                                <Table striped hover className="mb-0">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            {esSuperAdmin && (
                                                                <th className="text-center" style={{ width: "40px" }}>
                                                                    <Form.Check
                                                                        type="checkbox"
                                                                        checked={datosFiltrados.length > 0 && registrosSeleccionados.size === datosFiltrados.length}
                                                                        onChange={toggleSeleccionarTodos}
                                                                        title="Seleccionar todos los registros cargados"
                                                                    />
                                                                </th>
                                                            )}
                                                            <th>Fecha</th>
                                                            <th>Hora</th>
                                                            <th>Tipo</th>
                                                            <th>Empresa</th>
                                                            <th>Placa</th>
                                                            <th>Ruta</th>
                                                            <th>DNI Cond.</th>
                                                            <th>DNI Pasaj.</th>
                                                            <th>Nombre Pasaj.</th>
                                                            <th>Apellido Pasaj.</th>
                                                            <th>CECO</th>
                                                            <th>Cód. CECO</th>
                                                            <th>Ubicación</th>
                                                            <th>En Ruta?</th>
                                                            <th className="text-center">Acciones</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {datosPaginados.map((record, index) => {
                                                            const empleado = encontrarEmpleado(record);
                                                            const categoria = getCategoriaRegistro(record);
                                                            const badgeVariant = getCategoriaBadge(categoria);

                                                            // Priorizar el nombre guardado en el registro, si existe
                                                            let nombreMostrado = 'No encontrado';
                                                            if (record.nombre || record.apellidos) {
                                                                nombreMostrado = `${record.nombre || ''} ${record.apellidos || ''}`.trim();
                                                            } else if (empleado) {
                                                                nombreMostrado = `${empleado.nombres || empleado.name || ''} ${empleado.apellidoPaterno || empleado.apellidos || ''} ${empleado.apellidoMaterno || ''}`.trim();
                                                            }

                                                            const busId = record.busId || record.metadata?.busId;
                                                            const rutaId = record.rutaId || record.metadata?.rutaId;

                                                            // Intentar buscar bus por ID
                                                            let bus = buses.find(b => b.id === busId);

                                                            // Si no se encuentra por ID, intentar buscar por Placa
                                                            if (!bus) {
                                                                const placaBusqueda = record.busPlaca || record.metadata?.busPlaca;
                                                                if (placaBusqueda) {
                                                                    bus = buses.find(b => b.placa === placaBusqueda);
                                                                }
                                                            }
                                                            bus = bus || {};

                                                            const ruta = rutas.find(r => r.id === rutaId) || {};

                                                            const isTrackingGPS = record.tipo === "TRACKING" || record.metadata?.origen === "background_service" || categoria === "tracking";
                                                            const dniConductor = record.dniConductor || record.metadata?.driverDni;
                                                            const busPlaca = record.busPlaca || record.metadata?.busPlaca || bus.placa;

                                                            const conductor = driversCache[dniConductor] || (dniConductor ? "Cargando..." : "-");
                                                            const dniRegistro = getString(record.dni);
                                                            const dniSoloDigitos = dniRegistro.replace(/\D/g, "");
                                                            const esSystemOUnlinked = !dniRegistro || dniRegistro.toLowerCase() === "system" || (!record.nombre && !record.apellidos && !empleado);
                                                            const mostrarBotonActualizar = !isTrackingGPS && (esSystemOUnlinked || dniSoloDigitos.length > 9);

                                                            return (
                                                                <tr key={index}>
                                                                    {esSuperAdmin && (
                                                                        <td className="text-center">
                                                                            <Form.Check
                                                                                type="checkbox"
                                                                                checked={registrosSeleccionados.has(record.path)}
                                                                                onChange={() => toggleSeleccionRegistro(record.path)}
                                                                            />
                                                                        </td>
                                                                    )}
                                                                    <td>{record.fechaJornada}</td>
                                                                    <td>{record.fechaMarcacion || (record.timestamp ? format(record.timestamp.toDate(), 'HH:mm') : '')}</td>
                                                                    <td>
                                                                        <Badge bg={badgeVariant}>{getCategoriaLabel(categoria)}</Badge>
                                                                    </td>
                                                                    <td>{bus.empresaTransporte || "-"}</td>
                                                                    <td>
                                                                        {busPlaca ? (
                                                                            <Badge bg="secondary">{busPlaca}</Badge>
                                                                        ) : "-"}
                                                                    </td>
                                                                    <td>{ruta.nombre || "-"}</td>
                                                                    <td>{dniConductor || record.metadata?.driverDni || "-"}</td>
                                                                    <td title={`Ruta exacta en Firestore: ${record.path}`}>
                                                                        {isTrackingGPS ? (
                                                                            <Badge bg="secondary" title={`Ruta en Firestore: ${record.path}`}>SYSTEM (Tracking GPS)</Badge>
                                                                        ) : dniRegistro.toLowerCase() === "system" ? (
                                                                            <Badge bg="danger" title={`Ruta exacta en Firestore: ${record.path}`}>system (Sin vincular)</Badge>
                                                                        ) : (
                                                                            record.dni || "-"
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        {isTrackingGPS ? (
                                                                            <span className="fw-medium text-dark">{record.metadata?.driverRealName ? `Chofer: ${record.metadata.driverRealName}` : "Rastreo GPS Bus"}</span>
                                                                        ) : record.nombre || empleado?.nombres || empleado?.name ? (
                                                                            record.nombre || empleado?.nombres || empleado?.name
                                                                        ) : (
                                                                            <span className="text-muted fst-italic">Sin vincular</span>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        {isTrackingGPS ? (
                                                                            <span className="text-muted small">(Ubicación en 2do plano)</span>
                                                                        ) : record.apellidos || (empleado ? `${empleado.apellidoPaterno || empleado.apellidos || ''} ${empleado.apellidoMaterno || ''}`.trim() : '') ? (
                                                                            record.apellidos || (empleado ? `${empleado.apellidoPaterno || empleado.apellidos || ''} ${empleado.apellidoMaterno || ''}`.trim() : '')
                                                                        ) : (
                                                                            <span className="text-muted fst-italic">-</span>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        {empleado?.cecoNombre ? (
                                                                            <Badge bg="info" className="text-dark">
                                                                                {empleado.cecoNombre}
                                                                            </Badge>
                                                                        ) : (
                                                                            <span className="text-muted small">-</span>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        {(() => {
                                                                            const cecoData = cecosList.find(c =>
                                                                                c.id === empleado?.cecoId || (c.nombre && c.nombre === empleado?.cecoNombre)
                                                                            );
                                                                            return cecoData?.codigo ? (
                                                                                <Badge bg="dark">
                                                                                    {cecoData.codigo}
                                                                                </Badge>
                                                                            ) : (
                                                                                <span className="text-muted small">-</span>
                                                                            );
                                                                        })()}
                                                                    </td>
                                                                    <td>
                                                                        {(record.ubicacion?.latitud || record.latitud) && (record.ubicacion?.longitud || record.longitud) ? (
                                                                            <a
                                                                                href={`https://www.google.com/maps?q=${record.ubicacion?.latitud || record.latitud},${record.ubicacion?.longitud || record.longitud}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="text-danger"
                                                                                title="Ver en Google Maps"
                                                                            >
                                                                                <FaMapMarkerAlt />
                                                                            </a>
                                                                        ) : "-"}
                                                                    </td>
                                                                    <td>
                                                                        {(() => {
                                                                            const lat = record.ubicacion?.latitud || record.latitud;
                                                                            const lng = record.ubicacion?.longitud || record.longitud;
                                                                            const validacion = validarPuntoEnRuta(lat, lng, ruta);
                                                                            return (
                                                                                <Badge bg={validacion.color} title={`Desviación: ${Math.round(validacion.desviacion)}m`}>
                                                                                    {validacion.estado}
                                                                                </Badge>
                                                                            );
                                                                        })()}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {mostrarBotonActualizar && (
                                                                            <Button
                                                                                variant="outline-primary"
                                                                                size="sm"
                                                                                className="me-2"
                                                                                onClick={() => reintentarVinculacionRegistro(record)}
                                                                                title="Actualizar vínculo por legajo"
                                                                                disabled={actualizandoRegistros.has(`${record.path}::${record.id}`)}
                                                                            >
                                                                                {actualizandoRegistros.has(`${record.path}::${record.id}`) ? (
                                                                                    <Spinner animation="border" size="sm" />
                                                                                ) : (
                                                                                    <FaSyncAlt />
                                                                                )}
                                                                            </Button>
                                                                        )}
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            onClick={() => handleEliminar(record)}
                                                                            title="Eliminar Registro"
                                                                        >
                                                                            <FaTrash />
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </Card.Body>
                                        <Card.Footer className="bg-light">
                                            <Row className="align-items-center">
                                                <Col md={6}>
                                                    <Form.Select
                                                        value={elementosPorPagina}
                                                        onChange={(e) => {
                                                            setElementosPorPagina(Number(e.target.value));
                                                            setPaginaActual(1);
                                                        }}
                                                        size="sm"
                                                        style={{ width: "auto" }}
                                                    >
                                                        <option value={10}>10 por página</option>
                                                        <option value={25}>25 por página</option>
                                                        <option value={50}>50 por página</option>
                                                        <option value={100}>100 por página</option>
                                                    </Form.Select>
                                                </Col>
                                                <Col md={6} className="d-flex justify-content-end">
                                                    {renderPaginacion()}
                                                </Col>
                                            </Row>
                                        </Card.Footer>
                                    </Card>
                                )}
                            </>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </>
    );
};

export default ReporteIngresos;
